/* Bento product list carousel: arrows for the `layout: carousel` branch of
   `snippets/product-list-bento.liquid` and for the `carousel_mobile` variant of its
   grid — a grid on desktop, the same element turned into a scroll-snap track
   below 1024px. Fixed behaviour only: no autoplay, no loop, no pagination
   dots — those settings were removed from the block.

   The track is a native scroll-snap container (`assets/bento-blocks.css`), so
   the browser owns touch inertia and the snapping. This file moves the scroll
   position, mirrors it back into the arrows' disabled state, and adds the one
   drag the browser does not give us: a scroll container pans with touch and a
   trackpad, but a plain mouse has no click-drag. The markup contract it
   drives:
     - `.m-bento-product-list__grid--carousel[data-m-bento-carousel]`  root
     - `[data-m-bento-carousel-track]`   the scroll container, `tabindex="0"`;
       the `carousel_mobile` variant puts the attribute on the root itself, so
       the root *is* the track
     - `[data-m-bento-carousel-mobile="true"]` on the root   the carousel runs
       only while `(max-width: 1023px)` matches; this variant renders no
       arrows (CSS hides `.m-bento-product-list__nav` there too), so that
       control path no-ops
     - `[data-m-bento-carousel-desktop="true"]` on the root   the mirror
       image (`layout: carousel`, `layout_mobile: grid`): the classic
       carousel, run only while `(min-width: 1024px)` matches; below it CSS
       lays the track out as a grid and hides the arrows
     - `[data-m-bento-carousel-prev]` / `[data-m-bento-carousel-next]`   one
       card per click; the classic carousel's arrows are also hidden below
       1024px by CSS, where the row is swipe-only

   Images one screen ahead: the browser lazy-loads by distance to the
   viewport, and a card off to the side of a scrolled row counts as far away,
   so each card used to start its image only when it scrolled in. Once the
   row is near the viewport (`near`, an IntersectionObserver), `warmAhead()`
   switches the lazy images of every card within one track width past the
   visible edge to eager; it runs again on every scroll frame and size change,
   so the next screen is always fetching before it is shown. Hover images
   (`.m-bcard__media-hover`) stay lazy.

   There is no wrap at the ends: with the loop removed, running past an end
   simply does nothing and the arrow at that end shows `disabled`. The editor
   re-renders the section and hands us a fresh element, so the run is
   idempotent through `data-m-bento-carousel-init` on the root. Each carousel's
   listeners are also torn down on `shopify:section:unload` (see `handles`
   below), so an editor re-render never leaves a `resize` listener or a
   `ResizeObserver` running against a detached track. */
(function () {
  /* Idempotence for the top-level registration itself, same guard as
     `product-card-bento.js:20-25`: the editor can re-inject this script tag
     when a section reloads, and a second run would add a second
     `shopify:section:load`/`shopify:section:unload` document listener. */
  var behaviourRoot = document.documentElement;
  if (behaviourRoot.dataset.mBentoListBehaviour === 'true') return;
  behaviourRoot.dataset.mBentoListBehaviour = 'true';

  /* Sub-pixel layout leaves `scrollLeft` a fraction off the integer end, so a
     one-pixel band counts as being at it. */
  var EDGE = 1;

  /* Per-carousel teardown handles, keyed by the carousel root element so
     `shopify:section:unload` can find exactly the carousels it is about to
     detach. A plain object keyed by section id would work too, but a carousel
     element already exists everywhere it is needed and a `WeakMap` lets a
     detached element (and its handle) be collected once nothing else
     references it. */
  var handles = typeof WeakMap === 'function' ? new WeakMap() : null;

  function each(list, callback) {
    Array.prototype.forEach.call(list, callback);
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* The track carries `scroll-behavior: smooth`; asking for `smooth` here
     would override the reduced-motion rule the stylesheet sets, so the
     behaviour is decided per scroll, at scroll time. */
  function scrollBehaviour() {
    return reducedMotion() ? 'auto' : 'smooth';
  }

  /* ScrollToOptions is not universal; a plain assignment is the fallback and
     still honours the CSS `scroll-behavior`. */
  function scrollByLeft(track, delta, behavior) {
    if (typeof track.scrollBy === 'function') {
      try {
        track.scrollBy({ left: delta, behavior: behavior });
        return;
      } catch (error) {
        /* Engines that reject the options object fall through. */
      }
    }
    track.scrollLeft += delta;
  }

  function initCarousel(carousel) {
    /* The `carousel_mobile` variant puts `data-m-bento-carousel-track` on the
       root itself — the grid element doubles as the track; the classic
       carousel nests the track one level down. */
    var track = carousel.hasAttribute('data-m-bento-carousel-track')
      ? carousel
      : carousel.querySelector('[data-m-bento-carousel-track]');
    if (!track) return;

    /* Direct children only: the contract renders every item as a child of the
       track, and `.closest()`-style lookups must never pick up markup inside a
       product card. */
    var items = track.children;
    if (!items.length) return;

    var prev = carousel.querySelector('[data-m-bento-carousel-prev]');
    var next = carousel.querySelector('[data-m-bento-carousel-next]');

    /* `carousel_mobile` is a grid on desktop and a track only below the
       1024px line the stylesheet switches at, so the carousel runs only while
       `(max-width: 1023px)` matches. The desktop-only carousel
       (`data-m-bento-carousel-desktop`, a grid below 1024px) is the mirror
       image: it runs only while `(min-width: 1024px)` matches. The classic
       carousel has no such switch. Without `matchMedia` a variant fails
       open — it simply stays active. */
    var mobileOnly = carousel.getAttribute('data-m-bento-carousel-mobile') === 'true';
    var desktopOnly = carousel.getAttribute('data-m-bento-carousel-desktop') === 'true';
    var query = mobileOnly ? '(max-width: 1023px)' : desktopOnly ? '(min-width: 1024px)' : '';
    var breakpoint = query && window.matchMedia ? window.matchMedia(query) : null;

    var running = false;

    /* ---- geometry -------------------------------------------------------- */

    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    function atStart() {
      return track.scrollLeft <= EDGE;
    }

    function atEnd() {
      return track.scrollLeft >= maxScroll() - EDGE;
    }

    /* Exactly one card: its laid-out width plus the flex gap between items.
       Measured on every call rather than cached — a breakpoint change moves
       both numbers and no listener would know to re-measure. */
    function cardStep() {
      var first = items[0];
      if (!first) return 0;
      var width = first.getBoundingClientRect().width;
      var style = window.getComputedStyle(track);
      var gap = parseFloat(style.columnGap);
      if (!isFinite(gap)) gap = parseFloat(style.gap);
      if (!isFinite(gap)) gap = 0;
      return width + gap;
    }

    /* ---- drag ------------------------------------------------------------ */

    /* Mouse only: touch and pen already pan the scroll container natively, and
       calling `preventDefault` on their pointer events would take that away.
       The snap is switched off for the duration of the drag (CSS class), or
       every pointer move would be pulled back to the nearest card.

       A card is mostly links (image, title), so the drag starts on them too:
       the shopper grabs the row wherever the pointer is. Until the pointer has
       moved past DRAG_SLOP nothing is taken over, so a plain click still
       lands on the link; only then is the pointer captured (a capture from
       the press would retarget that click to the track) and the class set.
       The browser's own drag of a link or image (the ghost picture that
       followed the pointer) is cancelled on `dragstart`. Buttons and form
       controls in a card never start a drag. */
    var DRAG_SLOP = 4;
    var drag = null;

    function onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest && event.target.closest('button, input, select, textarea, label')) return;
      drag = { id: event.pointerId, x: event.clientX, startLeft: track.scrollLeft, moved: 0, active: false };
    }

    function onPointerMove(event) {
      if (!drag || event.pointerId !== drag.id) return;
      var dx = event.clientX - drag.x;
      if (Math.abs(dx) > drag.moved) drag.moved = Math.abs(dx);
      if (!drag.active) {
        if (drag.moved <= DRAG_SLOP) return;
        drag.active = true;
        carousel.classList.add('m-bento-product-list--dragging');
        if (window.getSelection) window.getSelection().removeAllRanges();
        if (track.setPointerCapture) {
          try {
            track.setPointerCapture(event.pointerId);
          } catch (error) {
            /* A pointer id the browser does not know (a synthetic event) cannot
               be captured; the drag still works through the move events. */
          }
        }
      }
      track.scrollLeft = drag.startLeft - dx;
      event.preventDefault();
    }

    function onDragStart(event) {
      event.preventDefault();
    }

    function onPointerUp(event) {
      if (!drag || event.pointerId !== drag.id) return;
      var moved = drag.active ? drag.moved : 0;
      drag = null;
      carousel.classList.remove('m-bento-product-list--dragging');
      if (track.releasePointerCapture) {
        try {
          track.releasePointerCapture(event.pointerId);
        } catch (error) {
          /* Already released - the capture is gone with the pointer. */
        }
      }
      /* A drag that moved must not land as a click on whatever card was under
         the pointer: swallow the one click that follows it. */
      if (moved > DRAG_SLOP) {
        track.addEventListener(
          'click',
          function swallow(clickEvent) {
            track.removeEventListener('click', swallow, true);
            clickEvent.preventDefault();
            clickEvent.stopPropagation();
          },
          true,
        );
      }
    }

    /* ---- controls -------------------------------------------------------- */

    /* One card in either direction. No loop: running past an end is a no-op —
       `sync()` keeps that arrow disabled, and this is the guard for the
       keyboard path too. */
    function move(direction) {
      var step = cardStep();
      if (step <= 0) return;
      if (direction < 0 ? atStart() : atEnd()) return;
      scrollByLeft(track, direction * step, scrollBehaviour());
    }

    var frame = null;

    /* ---- images ahead ---------------------------------------------------- */

    /* Set by the observer below; without IntersectionObserver the row warms
       from the start (the images are small, and the row works either way). */
    var near = typeof IntersectionObserver !== 'function';

    function warmAhead() {
      if (!near) return;
      var trackRect = track.getBoundingClientRect();
      /* A carousel in a hidden tab panel measures 0 wide: nothing is on
         screen to be ahead of. Showing the panel resizes the track, and the
         ResizeObserver below calls back in. */
      if (!trackRect.width) return;
      var limit = trackRect.right + trackRect.width;
      each(items, function (item) {
        if (item.getBoundingClientRect().left >= limit) return;
        each(item.querySelectorAll('img[loading="lazy"]'), function (img) {
          if (img.closest('.m-bcard__media-hover')) return;
          img.loading = 'eager';
        });
      });
    }

    function sync() {
      if (prev) prev.disabled = atStart();
      if (next) next.disabled = atEnd();
      warmAhead();
    }

    function onScroll() {
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        frame = null;
        sync();
      });
    }

    /* ---- wiring ---------------------------------------------------------- */

    function onPrevClick() {
      move(-1);
    }

    function onNextClick() {
      move(1);
    }

    /* The track itself is the only owner of the arrow keys: the cards inside
       hold links and buttons, and an arrow pressed there is not a carousel
       move. Without the guard the browser's own horizontal scroll would also
       fire, doubling the step. */
    function onKeydown(event) {
      if (event.target !== track) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(1);
      }
    }

    /* ---- lifecycle ------------------------------------------------------- */

    /* Everything the carousel owns is attached here and only here, so `stop()`
       has exactly one set to undo. The classic carousel starts once at boot
       and never stops; the mobile-only and desktop-only variants start and
       stop with their media query. */
    function start() {
      if (running) return;
      running = true;

      if (prev) prev.addEventListener('click', onPrevClick);
      if (next) next.addEventListener('click', onNextClick);

      track.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      track.addEventListener('keydown', onKeydown);

      track.addEventListener('pointerdown', onPointerDown);
      track.addEventListener('pointermove', onPointerMove);
      track.addEventListener('pointerup', onPointerUp);
      track.addEventListener('pointercancel', onPointerUp);
      track.addEventListener('dragstart', onDragStart);

      sync();
    }

    /* Detaches everything `start()` attached, but leaves `track.scrollLeft`
       alone: on desktop the stylesheet has already turned the element back
       into a grid, and writing a scroll position there would fight the
       layout. */
    function stop() {
      if (!running) return;
      running = false;

      if (prev) prev.removeEventListener('click', onPrevClick);
      if (next) next.removeEventListener('click', onNextClick);

      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      track.removeEventListener('keydown', onKeydown);

      track.removeEventListener('pointerdown', onPointerDown);
      track.removeEventListener('pointermove', onPointerMove);
      track.removeEventListener('pointerup', onPointerUp);
      track.removeEventListener('pointercancel', onPointerUp);
      track.removeEventListener('dragstart', onDragStart);

      drag = null;
      carousel.classList.remove('m-bento-product-list--dragging');

      if (frame) {
        window.cancelAnimationFrame(frame);
        frame = null;
      }
    }

    /* A carousel inside a hidden panel — a tab that is not the active one —
       measures 0 wide, so both arrows would be computed as "nothing to
       scroll" and stay disabled after the panel is shown. The observer
       re-syncs on every size change of the track, and showing the panel is
       one. It lives outside `start()`/`stop()` on purpose: it only mirrors
       geometry into the arrows' disabled state, which is harmless while the
       carousel is stopped. Kept as a variable (not a fire-and-forget) so
       `shopify:section:unload` can disconnect it — a re-render in the editor
       hands us a fresh track, but the old `ResizeObserver` keeps observing the
       detached one until something disconnects it. */
    var resizeObserver = null;
    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(function () {
        sync();
      });
      resizeObserver.observe(track);
    }

    /* "Near" is half a screen above or below: close enough that the shopper
       is about to see the row, far enough that a carousel at the bottom of a
       long page does not fetch its second screen on load. Once near, it stays
       near — the images already fetched are the whole cost. */
    var nearObserver = null;
    if (!near) {
      nearObserver = new IntersectionObserver(
        function (entries) {
          if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
          near = true;
          nearObserver.disconnect();
          nearObserver = null;
          warmAhead();
        },
        { rootMargin: '50% 0px' }
      );
      nearObserver.observe(track);
    }

    function onBreakpointChange() {
      if (breakpoint.matches) start();
      else stop();
    }

    /* A variant's query is watched for the whole life of the element, so a
       resize back across 1024px starts the carousel again. Removed on
       `shopify:section:unload` below, same reason as the `ResizeObserver`. */
    if (breakpoint) {
      if (typeof breakpoint.addEventListener === 'function') breakpoint.addEventListener('change', onBreakpointChange);
      else if (typeof breakpoint.addListener === 'function') breakpoint.addListener(onBreakpointChange);
      if (breakpoint.matches) start();
    } else {
      start();
    }

    if (handles) {
      handles.set(carousel, {
        stop: stop,
        teardown: function () {
          stop();
          if (resizeObserver) resizeObserver.disconnect();
          if (nearObserver) nearObserver.disconnect();
          if (breakpoint) {
            if (typeof breakpoint.removeEventListener === 'function') breakpoint.removeEventListener('change', onBreakpointChange);
            else if (typeof breakpoint.removeListener === 'function') breakpoint.removeListener(onBreakpointChange);
          }
        },
      });
    }
  }

  function boot() {
    each(document.querySelectorAll('[data-m-bento-carousel]'), function (carousel) {
      /* The editor re-renders the section, so the element itself is the guard:
         a fresh element has no marker and gets initialised once. */
      if (carousel.dataset.mBentoCarouselInit === 'true') return;
      carousel.dataset.mBentoCarouselInit = 'true';
      initCarousel(carousel);
    });
  }

  /* `event.target` is the section wrapper about to be detached. A carousel
     can be the wrapper's direct content (`product-list-bento`) or nested a
     level or two down (header mega panels), and in principle the wrapper
     itself could carry the attribute, so both the descendants and the
     wrapper are checked. Without this, every carousel torn down by the editor
     leaves its `resize` listener, `ResizeObserver` and (for the mobile-only
     and desktop-only variants) `matchMedia` listener running against
     elements no longer on screen. */
  function teardown(event) {
    if (!handles || !event.target || typeof event.target.querySelectorAll !== 'function') return;
    each(event.target.querySelectorAll('[data-m-bento-carousel]'), function (carousel) {
      var handle = handles.get(carousel);
      if (handle) {
        handle.teardown();
        handles.delete(carousel);
      }
    });
    if (typeof event.target.matches === 'function' && event.target.matches('[data-m-bento-carousel]')) {
      var ownHandle = handles.get(event.target);
      if (ownHandle) {
        ownHandle.teardown();
        handles.delete(event.target);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', teardown);

  /* Carousels injected after load — the product page's "Pairs well with"
     card fetches its list (assets/main-product-bento.js) — call this once
     the markup is in the document. `boot` skips carousels already running. */
  window.LeonartoBento = window.LeonartoBento || {};
  window.LeonartoBento.bootCarousels = boot;
})();
